import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useToast } from "../../hooks/useToast";
import Button from "../../components/common/Button";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ListLoadError from "../../components/admin/ListLoadError";
import api from "../../services/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import styles from "./AgencyDetailPage.module.css";

const AgencyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [agency, setAgency] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [statsError, setStatsError] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientsPagination, setClientsPagination] = useState(null);
  const [clientsError, setClientsError] = useState(null);
  const [clientsPage, setClientsPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [newlyGeneratedSsoSecret, setNewlyGeneratedSsoSecret] = useState(null);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoConfirmAction, setSsoConfirmAction] = useState(null);
  const [newlyGeneratedTestKey, setNewlyGeneratedTestKey] = useState(null);
  const [testKeyLoading, setTestKeyLoading] = useState(false);
  const [testKeyConfirmAction, setTestKeyConfirmAction] = useState(null);
  // Card 54617 — agent onboarding. The invite creates the account (inactive) and
  // emails a single-use setup link; the agent sets their own password.
  const [inviteForm, setInviteForm] = useState({ firstName: "", lastName: "", email: "" });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  useEffect(() => {
    loadAgency();
    loadMonthlyStats();
  }, [id]);

  useEffect(() => {
    loadClients(clientsPage);
  }, [id, clientsPage]);

  const loadAgency = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/agencies/${id}/metrics`);
      setAgency(response.agency);
    } catch (error) {
      // `services/api.js` sets `error.status` and never `error.response` -- the axios shape
      // made this branch unreachable, so a missing agency showed the generic failure toast.
      if (error?.status === 404) {
        navigate("/admin/agencies");
        toast.error("Agency not found");
      } else {
        toast.error("Failed to load agency");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyStats = async () => {
    try {
      setChartLoading(true);
      const response = await api.get(`/admin/agencies/${id}/monthly-stats`);
      setMonthlyStats(response.monthlyStats || []);
      setStatsError(null);
    } catch (error) {
      console.error("Failed to load monthly stats:", error);
      // A refused request is not a claim about this agency's history. Without this the
      // chart fell through to "No data available", which says the opposite.
      setStatsError(error.message || "Failed to load monthly stats");
      setMonthlyStats([]);
    } finally {
      setChartLoading(false);
    }
  };

  const loadClients = async (page = 1) => {
    try {
      setClientsLoading(true);
      const response = await api.get(`/admin/agencies/${id}/clients?page=${page}&limit=10`);
      setClients(response.clients || []);
      setClientsPagination(response.pagination || null);
      setClientsError(null);
    } catch (error) {
      console.error("Failed to load clients:", error);
      // Clearing the rows is load-bearing, NOT tidiness: setClients ran only on success,
      // so a failed PAGE CHANGE left the previous page's members on screen beneath the
      // new page number — real names presented as a page they are not on.
      setClientsError(error.message || "Failed to load clients");
      setClients([]);
      setClientsPagination(null);
    } finally {
      setClientsLoading(false);
    }
  };

  const handleGenerateApiKey = useCallback(async () => {
    try {
      setApiKeyLoading(true);
      setConfirmAction(null);
      const response = await api.post(`/admin/agencies/${id}/api-key`);
      setNewlyGeneratedKey(response.apiKey);
      setAgency((prev) => ({ ...prev, apiKey: `${response.apiKey.slice(0, 4)}...${response.apiKey.slice(-4)}`, apiKeyCreatedAt: new Date().toISOString() }));
      toast.success("API key generated successfully");
    } catch (error) {
      toast.error(error.message || "Failed to generate API key");
    } finally {
      setApiKeyLoading(false);
    }
  }, [id, toast]);

  const handleRevokeApiKey = useCallback(async () => {
    try {
      setApiKeyLoading(true);
      setConfirmAction(null);
      await api.delete(`/admin/agencies/${id}/api-key`);
      setNewlyGeneratedKey(null);
      setAgency((prev) => ({ ...prev, apiKey: null, apiKeyCreatedAt: null }));
      toast.success("API key revoked successfully");
    } catch (error) {
      toast.error(error.message || "Failed to revoke API key");
    } finally {
      setApiKeyLoading(false);
    }
  }, [id, toast]);

  const handleCopyKey = useCallback(async () => {
    if (!newlyGeneratedKey) return;
    try {
      await navigator.clipboard.writeText(newlyGeneratedKey);
      toast.success("API key copied to clipboard");
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = newlyGeneratedKey;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("API key copied to clipboard");
    }
  }, [newlyGeneratedKey, toast]);

  const handleGenerateSsoSecret = useCallback(async () => {
    try {
      setSsoLoading(true);
      setSsoConfirmAction(null);
      const response = await api.post(`/admin/agencies/${id}/sso-secret`);
      setNewlyGeneratedSsoSecret(response.ssoSecret);
      setAgency((prev) => ({ ...prev, ssoSecret: `${response.ssoSecret.slice(0, 4)}...${response.ssoSecret.slice(-4)}`, ssoEnabled: prev.ssoEnabled }));
      toast.success("SSO secret generated successfully");
    } catch (error) {
      toast.error(error.message || "Failed to generate SSO secret");
    } finally {
      setSsoLoading(false);
    }
  }, [id, toast]);

  const handleRevokeSsoSecret = useCallback(async () => {
    try {
      setSsoLoading(true);
      setSsoConfirmAction(null);
      await api.delete(`/admin/agencies/${id}/sso-secret`);
      setNewlyGeneratedSsoSecret(null);
      setAgency((prev) => ({ ...prev, ssoSecret: null, ssoEnabled: false }));
      toast.success("SSO secret revoked");
    } catch (error) {
      toast.error(error.message || "Failed to revoke SSO secret");
    } finally {
      setSsoLoading(false);
    }
  }, [id, toast]);

  const handleToggleSso = useCallback(async () => {
    try {
      setSsoLoading(true);
      const response = await api.patch(`/admin/agencies/${id}/sso-toggle`);
      setAgency((prev) => ({ ...prev, ssoEnabled: response.ssoEnabled }));
      toast.success(`SSO ${response.ssoEnabled ? "enabled" : "disabled"}`);
    } catch (error) {
      toast.error(error.message || "Failed to toggle SSO");
    } finally {
      setSsoLoading(false);
    }
  }, [id, toast]);

  const handleCopySsoSecret = useCallback(async () => {
    if (!newlyGeneratedSsoSecret) return;
    try {
      await navigator.clipboard.writeText(newlyGeneratedSsoSecret);
      toast.success("SSO secret copied to clipboard");
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = newlyGeneratedSsoSecret;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("SSO secret copied to clipboard");
    }
  }, [newlyGeneratedSsoSecret, toast]);

  const handleGenerateTestKey = useCallback(async () => {
    try {
      setTestKeyLoading(true);
      setTestKeyConfirmAction(null);
      const response = await api.post(`/admin/agencies/${id}/test-api-key`);
      setNewlyGeneratedTestKey(response.testApiKey);
      setAgency((prev) => ({ ...prev, testApiKey: `${response.testApiKey.slice(0, 4)}...${response.testApiKey.slice(-4)}`, testApiKeyCreatedAt: new Date().toISOString() }));
      toast.success("Test API key generated successfully");
    } catch (error) {
      toast.error(error.message || "Failed to generate test API key");
    } finally {
      setTestKeyLoading(false);
    }
  }, [id, toast]);

  const handleRevokeTestKey = useCallback(async () => {
    try {
      setTestKeyLoading(true);
      setTestKeyConfirmAction(null);
      await api.delete(`/admin/agencies/${id}/test-api-key`);
      setNewlyGeneratedTestKey(null);
      setAgency((prev) => ({ ...prev, testApiKey: null, testApiKeyCreatedAt: null }));
      toast.success("Test API key revoked successfully");
    } catch (error) {
      toast.error(error.message || "Failed to revoke test API key");
    } finally {
      setTestKeyLoading(false);
    }
  }, [id, toast]);

  const handleCopyTestKey = useCallback(async () => {
    if (!newlyGeneratedTestKey) return;
    try {
      await navigator.clipboard.writeText(newlyGeneratedTestKey);
      toast.success("Test API key copied to clipboard");
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = newlyGeneratedTestKey;
      textArea.style.position = "fixed";
      textArea.style.opacity = 0;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("Test API key copied to clipboard");
    }
  }, [newlyGeneratedTestKey, toast]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  // Card 54617. `api.js` THROWS on non-2xx and carries the parsed body on
  // `error.data`, so the 409's message has to be read from there — branching on
  // `error.message` alone would still work but loses the rest of the body.
  const handleInviteAgent = useCallback(async (event) => {
    event.preventDefault();
    if (!inviteForm.firstName.trim() || !inviteForm.lastName.trim() || !inviteForm.email.trim()) {
      toast.error("First name, last name and email are required");
      return;
    }

    try {
      setInviteLoading(true);
      setInviteResult(null);
      const response = await api.post(`/admin/agencies/${id}/agents/invite`, {
        firstName: inviteForm.firstName.trim(),
        lastName: inviteForm.lastName.trim(),
        email: inviteForm.email.trim()
      });
      setInviteResult({ ok: true, message: response.message });
      toast.success(response.emailSent ? "Agent invited successfully" : response.message);
      setInviteForm({ firstName: "", lastName: "", email: "" });
    } catch (error) {
      const message = error?.data?.message || error?.data?.error || error?.message || "Failed to invite agent";
      setInviteResult({ ok: false, message });
      toast.error(message);
    } finally {
      setInviteLoading(false);
    }
  }, [id, inviteForm, toast]);

  const updateInviteField = (field) => (event) => {
    const { value } = event.target;
    setInviteForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className={styles.loadingPage}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!agency) {
    return (
      <div className={styles.notFound}>
        <h2>Agency not found</h2>
        <Button onClick={() => navigate("/admin/agencies")}>Back to Agencies</Button>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate("/admin/agencies")}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5"></path>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Agencies
        </button>

        <div className={styles.headerContent}>
          <div className={styles.headerInfo}>
            <h1>{agency.name}</h1>
            <div className={styles.headerMeta}>
              <span>Created {formatDate(agency.createdAt)}</span>
              <span className={`${styles.statusBadge} ${agency.isActive ? styles.active : styles.inactive}`}>
                {agency.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
          <Button onClick={() => navigate(`/admin/agencies?edit=${agency.id}`)}>Edit Agency</Button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className={styles.metricsGrid}>
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{agency.totalClients || 0}</div>
          <div className={styles.metricLabel}>Total Clients</div>
        </div>
        <div className={styles.metricCard}>
          <div className={`${styles.metricValue} ${styles.green}`}>{agency.activeSubscriptions || 0}</div>
          <div className={styles.metricLabel}>Active Subscriptions</div>
        </div>
        <div className={styles.metricCard}>
          <div className={`${styles.metricValue} ${styles.gray}`}>{agency.cancelledSubscriptions || 0}</div>
          <div className={styles.metricLabel}>Cancelled</div>
        </div>
      </div>

      {/* Agent Access (card 54617) */}
      <div className={styles.apiKeySection}>
        <h2>Agent Access</h2>
        <p className={styles.testKeyNote}>
          Invite an agent to {agency.name}. They receive a single-use setup link that expires in 72 hours;
          setting a password creates their account and adds them to this agency.
        </p>
        <form className={styles.agentInviteForm} onSubmit={handleInviteAgent}>
          <div className={styles.agentInviteRow}>
            <input
              className={styles.agentInviteInput}
              type="text"
              placeholder="First name"
              aria-label="Agent first name"
              value={inviteForm.firstName}
              onChange={updateInviteField("firstName")}
            />
            <input
              className={styles.agentInviteInput}
              type="text"
              placeholder="Last name"
              aria-label="Agent last name"
              value={inviteForm.lastName}
              onChange={updateInviteField("lastName")}
            />
            <input
              className={styles.agentInviteInput}
              type="email"
              placeholder="agent@agency.com"
              aria-label="Agent email"
              value={inviteForm.email}
              onChange={updateInviteField("email")}
            />
            <Button type="submit" disabled={inviteLoading}>
              {inviteLoading ? "Inviting..." : "Invite Agent"}
            </Button>
          </div>
          {inviteResult && (
            <p className={inviteResult.ok ? styles.inviteSuccess : styles.inviteError} role="status">
              {inviteResult.message}
            </p>
          )}
        </form>
      </div>

      {/* API Key Management */}
      <div className={styles.apiKeySection}>
        <h2>Partner API Key</h2>
        <div className={styles.apiKeyContent}>
          {newlyGeneratedKey ? (
            <div className={styles.apiKeyDisplay}>
              <div className={styles.apiKeyNewBanner}>
                Save this key now — it will not be shown again
              </div>
              <div className={styles.apiKeyRow}>
                <code className={styles.apiKeyFull}>{newlyGeneratedKey}</code>
                <button className={styles.copyBtn} onClick={handleCopyKey} title="Copy to clipboard">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                  </svg>
                  Copy
                </button>
              </div>
            </div>
          ) : agency.apiKey ? (
            <div className={styles.apiKeyDisplay}>
              <div className={styles.apiKeyRow}>
                <code className={styles.apiKeyMasked}>{agency.apiKey}</code>
                <span className={styles.apiKeyDate}>Created {formatDate(agency.apiKeyCreatedAt)}</span>
              </div>
            </div>
          ) : (
            <p className={styles.apiKeyEmpty}>No API key configured</p>
          )}

          <div className={styles.apiKeyActions}>
            {confirmAction ? (
              <div className={styles.confirmBar}>
                <span>{confirmAction === "regenerate" ? "Regenerate key? The current key will stop working." : "Revoke key? API access will be disabled."}</span>
                <button
                  className={styles.confirmYes}
                  onClick={confirmAction === "regenerate" ? handleGenerateApiKey : handleRevokeApiKey}
                  disabled={apiKeyLoading}
                >
                  {apiKeyLoading ? "..." : "Confirm"}
                </button>
                <button className={styles.confirmNo} onClick={() => setConfirmAction(null)}>Cancel</button>
              </div>
            ) : (
              <>
                {!agency.apiKey ? (
                  <button className={styles.generateBtn} onClick={handleGenerateApiKey} disabled={apiKeyLoading}>
                    {apiKeyLoading ? "Generating..." : "Generate API Key"}
                  </button>
                ) : (
                  <>
                    <button className={styles.regenerateBtn} onClick={() => setConfirmAction("regenerate")} disabled={apiKeyLoading}>
                      Regenerate
                    </button>
                    <button className={styles.revokeBtn} onClick={() => setConfirmAction("revoke")} disabled={apiKeyLoading}>
                      Revoke
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* SSO Settings */}
      <div className={styles.apiKeySection}>
        <h2>SSO Settings</h2>
        <div className={styles.apiKeyContent}>
          {/* SSO Toggle */}
          {agency.ssoSecret && (
            <div className={styles.ssoToggleRow}>
              <span className={styles.ssoToggleLabel}>Single Sign-On</span>
              <button
                className={`${styles.ssoToggle} ${agency.ssoEnabled ? styles.ssoToggleOn : ""}`}
                onClick={handleToggleSso}
                disabled={ssoLoading}
                title={agency.ssoEnabled ? "Disable SSO" : "Enable SSO"}
              >
                <span className={styles.ssoToggleKnob} />
              </button>
              <span className={styles.ssoToggleStatus}>{agency.ssoEnabled ? "Enabled" : "Disabled"}</span>
            </div>
          )}

          {/* SSO Secret Display */}
          {newlyGeneratedSsoSecret ? (
            <div className={styles.apiKeyDisplay}>
              <div className={styles.apiKeyNewBanner}>
                Save this secret now — it will not be shown again
              </div>
              <div className={styles.apiKeyRow}>
                <code className={styles.apiKeyFull}>{newlyGeneratedSsoSecret}</code>
                <button className={styles.copyBtn} onClick={handleCopySsoSecret} title="Copy to clipboard">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                  </svg>
                  Copy
                </button>
              </div>
            </div>
          ) : agency.ssoSecret ? (
            <div className={styles.apiKeyDisplay}>
              <div className={styles.apiKeyRow}>
                <code className={styles.apiKeyMasked}>{agency.ssoSecret}</code>
              </div>
            </div>
          ) : (
            <p className={styles.apiKeyEmpty}>No SSO secret configured</p>
          )}

          {/* SSO Actions */}
          <div className={styles.apiKeyActions}>
            {ssoConfirmAction ? (
              <div className={styles.confirmBar}>
                <span>{ssoConfirmAction === "regenerate" ? "Regenerate secret? The current secret will stop working." : "Revoke secret? SSO will be disabled."}</span>
                <button
                  className={styles.confirmYes}
                  onClick={ssoConfirmAction === "regenerate" ? handleGenerateSsoSecret : handleRevokeSsoSecret}
                  disabled={ssoLoading}
                >
                  {ssoLoading ? "..." : "Confirm"}
                </button>
                <button className={styles.confirmNo} onClick={() => setSsoConfirmAction(null)}>Cancel</button>
              </div>
            ) : (
              <>
                {!agency.ssoSecret ? (
                  <button className={styles.generateBtn} onClick={handleGenerateSsoSecret} disabled={ssoLoading}>
                    {ssoLoading ? "Generating..." : "Generate SSO Secret"}
                  </button>
                ) : (
                  <>
                    <button className={styles.regenerateBtn} onClick={() => setSsoConfirmAction("regenerate")} disabled={ssoLoading}>
                      Regenerate
                    </button>
                    <button className={styles.revokeBtn} onClick={() => setSsoConfirmAction("revoke")} disabled={ssoLoading}>
                      Revoke
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Test API Key */}
      <div className={styles.apiKeySection}>
        <h2>Test API Key</h2>
        <p className={styles.testKeyNote}>For sandbox/testing integrations only. Does not affect production.</p>
        <div className={styles.apiKeyContent}>
          {newlyGeneratedTestKey ? (
            <div className={styles.apiKeyDisplay}>
              <div className={styles.apiKeyNewBanner}>
                Save this test key now — it will not be shown again
              </div>
              <div className={styles.apiKeyRow}>
                <code className={styles.apiKeyFull}>{newlyGeneratedTestKey}</code>
                <button className={styles.copyBtn} onClick={handleCopyTestKey} title="Copy to clipboard">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                  </svg>
                  Copy
                </button>
              </div>
            </div>
          ) : agency.testApiKey ? (
            <div className={styles.apiKeyDisplay}>
              <div className={styles.apiKeyRow}>
                <code className={styles.apiKeyMasked}>{agency.testApiKey}</code>
                <span className={styles.apiKeyDate}>Created {formatDate(agency.testApiKeyCreatedAt)}</span>
              </div>
            </div>
          ) : (
            <p className={styles.apiKeyEmpty}>No test API key configured</p>
          )}

          <div className={styles.apiKeyActions}>
            {testKeyConfirmAction ? (
              <div className={styles.confirmBar}>
                <span>{testKeyConfirmAction === "regenerate" ? "Regenerate test key? The current test key will stop working." : "Revoke test key?"}</span>
                <button
                  className={styles.confirmYes}
                  onClick={testKeyConfirmAction === "regenerate" ? handleGenerateTestKey : handleRevokeTestKey}
                  disabled={testKeyLoading}
                >
                  {testKeyLoading ? "..." : "Confirm"}
                </button>
                <button className={styles.confirmNo} onClick={() => setTestKeyConfirmAction(null)}>Cancel</button>
              </div>
            ) : (
              <>
                {!agency.testApiKey ? (
                  <button className={styles.generateBtn} onClick={handleGenerateTestKey} disabled={testKeyLoading}>
                    {testKeyLoading ? "Generating..." : "Generate Test Key"}
                  </button>
                ) : (
                  <>
                    <button className={styles.regenerateBtn} onClick={() => setTestKeyConfirmAction("regenerate")} disabled={testKeyLoading}>
                      Regenerate
                    </button>
                    <button className={styles.revokeBtn} onClick={() => setTestKeyConfirmAction("revoke")} disabled={testKeyLoading}>
                      Revoke
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>


      <div className={styles.chartSection}>
        <h2>Monthly Trends</h2>
        {chartLoading ? (
          <div className={styles.chartLoading}>
            <LoadingSpinner />
          </div>
        ) : statsError ? (
          <ListLoadError message="Could not load monthly trends" onRetry={loadMonthlyStats} />
        ) : monthlyStats.length === 0 ? (
          <div className={styles.chartEmpty}>No data available</div>
        ) : (
          <div className={styles.chartContainer}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyStats} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3e5d0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#737373" />
                <YAxis tick={{ fontSize: 12 }} stroke="#737373" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "0.5px solid #f3e5d0",
                    borderRadius: "8px",
                    fontSize: "14px"
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="signups" stroke="#f97316" strokeWidth={2} name="Signups" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="active" stroke="#059669" strokeWidth={2} name="Active" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="cancelled" stroke="#6b7280" strokeWidth={2} name="Cancelled" dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Clients Table */}
      <div className={styles.clientsSection}>
        <h2>Clients</h2>
        <div className={styles.tableCard}>
          {clientsLoading ? (
            <div className={styles.tableLoading}>
              <LoadingSpinner />
            </div>
          ) : clientsError ? (
            <ListLoadError message="Could not load clients" onRetry={() => loadClients(clientsPage)} />
          ) : clients.length === 0 ? (
            <div className={styles.tableEmpty}>No clients yet</div>
          ) : (
            <>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Signup Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client) => (
                    <tr key={client.id} onClick={() => navigate(`/admin/members/${client.id}`)} className={styles.clickableRow}>
                      <td>{client.name}</td>
                      <td>{client.email}</td>
                      <td>{formatDate(client.signupDate)}</td>
                      <td>
                        <span className={`${styles.clientStatus} ${client.status === "active" ? styles.active : styles.inactive}`}>
                          {client.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {clientsPagination && clientsPagination.totalPages > 1 && (
                <div className={styles.pagination}>
                  <button
                    disabled={clientsPage === 1}
                    onClick={() => setClientsPage(clientsPage - 1)}
                    className={styles.pageBtn}
                  >
                    Previous
                  </button>
                  <span>Page {clientsPage} of {clientsPagination.totalPages}</span>
                  <button
                    disabled={clientsPage === clientsPagination.totalPages}
                    onClick={() => setClientsPage(clientsPage + 1)}
                    className={styles.pageBtn}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgencyDetailPage;
